import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';

type RetryButtonProps = {
  retryAfter: number | null;
  onRetry: () => void;
  label?: string;
};

/**
 * "Try again" button for retryable API errors. When `retryAfter` (seconds)
 * is provided, the button stays disabled and counts down before becoming
 * clickable, matching the server's suggested retry delay.
 */
export function RetryButton({ retryAfter, onRetry, label = 'Try again' }: RetryButtonProps) {
  const [secondsLeft, setSecondsLeft] = useState(retryAfter ?? 0);

  useEffect(() => {
    setSecondsLeft(retryAfter ?? 0);
  }, [retryAfter]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [secondsLeft]);

  const isWaiting = secondsLeft > 0;

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      onClick={onRetry}
      disabled={isWaiting}
      className="gap-1.5 border-[#026857] text-[#026857] hover:bg-[#026857]/5 hover:text-[#026857]"
    >
      <RefreshCw size={14} />
      {isWaiting ? `${label} in ${secondsLeft}s` : label}
    </Button>
  );
}
